import React, { useState } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator, Text, Platform, Alert, Modal } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useActionSheet } from '@expo/react-native-action-sheet';
import theme from '@/config/theme';
import { useWebAlert, AlertButton } from '@/components/WebAlert';
import { uploadDevChecklistImage, deleteDevChecklistImage } from '@/services/dev/devChecklistImageService';
import { DevChecklistEditorLogger } from '@/utils/logger';
import { pickImageFromLibrary, takePhotoFromCamera } from '@/utils/media/imagePicker';

interface DevChecklistPhotoFieldProps {
  empresaId: string;
  checklistId: string;
  fieldId: string;
  value?: string;
  onChange: (uri: string) => void;
  readOnly?: boolean;
}

const logger = DevChecklistEditorLogger.Image;

const DevChecklistPhotoField: React.FC<DevChecklistPhotoFieldProps> = ({
  empresaId,
  checklistId,
  fieldId,
  value,
  onChange,
  readOnly = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewUri, setPreviewUri] = useState<string | null>(null);
  const [viewingPhoto, setViewingPhoto] = useState(false);
  const { showActionSheetWithOptions } = useActionSheet();
  const { showAlert } = useWebAlert();

  const showMessage = (title: string, msg: string, buttons?: AlertButton[]) => {
    if (Platform.OS !== 'android') {
      showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
    } else {
      Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
    }
  };

  const handlePickImage = () => {
    if (readOnly) {
      showMessage('Somente leitura', 'Alterações de foto estão temporariamente desabilitadas.');
      return;
    }

    showActionSheetWithOptions(
      {
        options: ['Cancelar', 'Tirar foto', 'Galeria'],
        cancelButtonIndex: 0,
        title: 'Selecionar foto',
        message: 'De onde você quer obter a foto?',
        titleTextStyle: {
          color: theme.colors.text.primary,
          fontSize: theme.fontSizes.md,
          fontWeight: theme.fontWeights.medium as any,
        },
        messageTextStyle: {
          color: theme.colors.text.secondary,
          fontSize: theme.fontSizes.sm,
        },
        containerStyle: {
          backgroundColor: theme.colors.background.primary,
        },
        textStyle: {
          color: theme.colors.primary.default,
          fontSize: theme.fontSizes.md,
        },
      },
      async (buttonIndex) => {
        if (buttonIndex === 1) {
          await takePhoto();
        }
        if (buttonIndex === 2) {
          await pickFromLibrary();
        }
      }
    );
  };

  const takePhoto = async () => {
    try {
      const outcome = await takePhotoFromCamera({
        mediaTypes: ['images'],
        allowsEditing: false,
        aspect: [4, 3],
        quality: 0.8,
      }, {
        source: 'dev_checklist_photo_field',
        screen: 'components/checklist/dev/DevChecklistPhotoField',
        action: 'take_dev_checklist_photo_with_camera',
        entityType: 'dev_checklist_photo',
        entityId: fieldId,
        checklistId,
        empresaId,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar a câmera.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.warn('[DevChecklistPhotoField] Falha ao abrir camera', outcome.error);
        showMessage('Erro', 'Não foi possível tirar a foto.');
        return;
      }

      if (outcome.status === 'success' && outcome.result.assets?.length) {
        const asset = outcome.result.assets[0];
        await handleSelectedImage(asset.uri);
      }
    } catch (error) {
      showMessage('Erro', 'Não foi possível tirar a foto.');
    }
  };

  const pickFromLibrary = async () => {
    try {
      const outcome = await pickImageFromLibrary({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: false,
        aspect: [4, 3],
        quality: 0.8,
      }, {
        source: 'dev_checklist_photo_field',
        screen: 'components/checklist/dev/DevChecklistPhotoField',
        action: 'pick_dev_checklist_photo_from_library',
        entityType: 'dev_checklist_photo',
        entityId: fieldId,
        checklistId,
        empresaId,
      });

      if (outcome.status === 'denied') {
        showMessage('Permissão negada', 'Precisamos de permissão para acessar suas fotos.');
        return;
      }

      if (outcome.status === 'failed') {
        logger.warn('[DevChecklistPhotoField] Falha ao abrir galeria', outcome.error);
        showMessage('Erro', 'Não foi possível selecionar a imagem.');
        return;
      }

      if (outcome.status !== 'success') {
        return;
      }

      const asset = outcome.result.assets[0];
      if (!asset) return;
      await handleSelectedImage(asset.uri);
    } catch (error) {
      logger.warn('[DevChecklistPhotoField] Erro inesperado ao selecionar imagem', error);
      showMessage('Erro', 'Não foi possível selecionar a imagem.');
    }
  };

  const handleSelectedImage = async (uri: string) => {
    if (readOnly) {
      showMessage('Somente leitura', 'Alterações de foto estão temporariamente desabilitadas.');
      return;
    }

    setUploading(true);
    setPreviewUri(uri);
    try {
      const result = await uploadDevChecklistImage({
        empresaId,
        checklistId,
        campoId: fieldId,
        localUri: uri,
        kind: 'foto',
      });
      onChange(result.publicUrl);
      setPreviewUri(null);
      showMessage('Sucesso', 'Foto salva com sucesso!');
    } catch (error: any) {
      logger.warn('[DevChecklistPhotoField] Falha ao fazer upload', error);
      showMessage('Erro', error?.message || 'Falha ao enviar a foto.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    if (readOnly) {
      showMessage('Somente leitura', 'Remoções de foto estão temporariamente desabilitadas.');
      return;
    }

    const confirmRemove = () => {
      showMessage(
        'Confirmar remoção',
        'Tem certeza que deseja remover esta foto?',
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Remover', style: 'destructive', onPress: performRemove },
        ]
      );
    };

    const performRemove = async () => {
      setDeleting(true);
      try {
        await deleteDevChecklistImage(value);
        onChange('');
        showMessage('Sucesso', 'Foto removida com sucesso!');
      } catch (error: any) {
        logger.warn('[DevChecklistPhotoField] Falha ao remover foto', error);
        showMessage('Erro', error?.message || 'Falha ao remover a foto.');
      } finally {
        setDeleting(false);
      }
    };

    confirmRemove();
  };

  const uriToShow = previewUri ?? value ?? null;

  return (
    <View style={styles.container}>
      <Text style={styles.label}>Foto</Text>
      <View style={styles.imageWrapper}>
        {uriToShow ? (
          <TouchableOpacity onPress={() => setViewingPhoto(true)}>
            <Image source={{ uri: uriToShow }} style={styles.image} />
          </TouchableOpacity>
        ) : (
          <View style={styles.placeholder}>
            <MaterialCommunityIcons name="camera" size={40} color={theme.colors.text.inverse} />
          </View>
        )}

        {uriToShow ? (
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={styles.button}
              onPress={handlePickImage}
              disabled={readOnly || uploading || deleting}
            >
              {uploading ? (
                <ActivityIndicator size="small" color={theme.colors.primary.default} />
              ) : (
                <Text style={styles.buttonText}>Alterar foto</Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.buttonDanger]}
              onPress={handleRemovePhoto}
              disabled={readOnly || uploading || deleting}
            >
              {deleting ? (
                <ActivityIndicator size="small" color={theme.colors.util.error} />
              ) : (
                <Text style={[styles.buttonText, styles.buttonDangerText]}>Remover</Text>
              )}
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity
            style={styles.button}
            onPress={handlePickImage}
            disabled={readOnly || uploading}
          >
            {uploading ? (
              <ActivityIndicator size="small" color={theme.colors.primary.default} />
            ) : (
              <Text style={styles.buttonText}>Adicionar foto</Text>
            )}
          </TouchableOpacity>
        )}
      </View>

      <Modal visible={viewingPhoto} transparent animationType="fade">
        <View style={styles.modalBackdrop}>
          <TouchableOpacity style={styles.modalBackdrop} onPress={() => setViewingPhoto(false)}>
            {uriToShow ? <Image source={{ uri: uriToShow }} style={styles.modalImage} /> : null}
          </TouchableOpacity>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  label: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.xs,
  },
  imageWrapper: {
    gap: theme.spacing.sm,
  },
  image: {
    width: '100%',
    height: 220,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.background.secondary,
  },
  placeholder: {
    height: 220,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.background.tertiary,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  button: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
    fontWeight: '500' as any,
  },
  buttonDanger: {
    borderColor: theme.colors.util.error,
  },
  buttonDangerText: {
    color: theme.colors.util.error,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.75)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalImage: {
    width: '90%',
    height: '80%',
    resizeMode: 'contain',
  },
});

export default DevChecklistPhotoField;
